import { useTranslation } from 'react-i18next'
import dayjs from 'dayjs'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/shadcn/table'
import { Badge } from '@/components/shadcn/badge'
import { Button } from '@/components/shadcn/button'
import { SortableTableHead } from '@/components/common/SortableTableHead'
import { LoaderTable } from '@/components/common/loaders/LoaderTable'
import { TimeTrackingApproval } from '@/api/model'
import type { OrderType, TimesheetModel } from '@/api/model'
import { TIMETRACKING_STATUS_CLASS, getTimeTrackingStatusLabel } from './timeTrackingLabels'

/** Vom Company-Endpunkt akzeptierte Sortierfelder (s. altes Portal). */
export const TimeTrackingSortFields = {
  EmployeeName: 'employeeUserId',
  StartTimestamp: 'startTimestamp',
  EndTimestamp: 'endTimestamp',
  NetWorkingHours: 'netWorkingHours',
} as const

interface Props {
  rows: TimesheetModel[] | undefined | null
  isLoading: boolean
  isError: boolean
  currentSort?: string
  currentOrder?: OrderType
  onSort: (sort?: string, order?: OrderType) => void
  updatingId: number | null
  onUpdateStatus: (entry: TimesheetModel, status: TimeTrackingApproval) => void
}

const COL_COUNT = 7

const formatDateTime = (value: string) => dayjs(value).format('DD.MM.YYYY HH:mm')
const formatTime = (value: string) => dayjs(value).format('HH:mm')

export function TimeTrackingEntriesDataTable({
  rows,
  isLoading,
  isError,
  currentSort,
  currentOrder,
  onSort,
  updatingId,
  onUpdateStatus,
}: Props) {
  const { t } = useTranslation(['service-modules-timetracking', 'service-modules', 'common'])
  return (
    <Table>
      <TableHeader className="sticky top-0 z-10 bg-card shadow-sm [&_th]:bg-card">
        <TableRow>
          <SortableTableHead field={TimeTrackingSortFields.EmployeeName} currentSort={currentSort} currentOrder={currentOrder} onSort={onSort}>
            {t('columns.employee', { ns: 'common' })}
          </SortableTableHead>
          <SortableTableHead field={TimeTrackingSortFields.StartTimestamp} currentSort={currentSort} currentOrder={currentOrder} onSort={onSort}>
            {t('shared.columns.start', { ns: 'service-modules' })}
          </SortableTableHead>
          <SortableTableHead field={TimeTrackingSortFields.EndTimestamp} currentSort={currentSort} currentOrder={currentOrder} onSort={onSort}>
            {t('shared.columns.end', { ns: 'service-modules' })}
          </SortableTableHead>
          <SortableTableHead field={TimeTrackingSortFields.NetWorkingHours} currentSort={currentSort} currentOrder={currentOrder} onSort={onSort} className="text-right">
            {t('timetracking.table.netWorkingHours')}
          </SortableTableHead>
          <TableHead>{t('timetracking.table.pauses')}</TableHead>
          <TableHead>{t('columns.status', { ns: 'common' })}</TableHead>
          <TableHead className="text-right">{t('columns.actions', { ns: 'common' })}</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        <LoaderTable
          isLoading={isLoading}
          isError={isError}
          isEmpty={!rows || rows.length === 0}
          colSpan={COL_COUNT}
          errorMessage={t('timetracking.table.loadError')}
          emptyMessage={t('timetracking.table.empty')}
        >
          {rows?.map((entry) => {
          const isUpdating = updatingId === entry.id
          const approval = entry.approval ?? TimeTrackingApproval.Pending
          const pauses = entry.pauses ?? []
          return (
            <TableRow key={entry.id}>
              <TableCell>{entry.employeeName || '—'}</TableCell>
              <TableCell className="tabular-nums">{formatDateTime(entry.startTimestamp)}</TableCell>
              <TableCell className="tabular-nums">{formatDateTime(entry.endTimestamp)}</TableCell>
              <TableCell className="text-right tabular-nums">{entry.netWorkingHours ?? '—'}</TableCell>
              <TableCell>
                {pauses.length === 0 ? (
                  '—'
                ) : (
                  <div className="flex flex-col gap-0.5">
                    {pauses.map((pause, index) => (
                      <span key={`${pause.startTimestamp}-${index}`} className="tabular-nums text-sm">
                        {`${formatTime(pause.startTimestamp)} – ${formatTime(pause.endTimestamp)}`}
                      </span>
                    ))}
                  </div>
                )}
              </TableCell>
              <TableCell>
                <Badge className={TIMETRACKING_STATUS_CLASS[approval]}>
                  {getTimeTrackingStatusLabel(t, approval)}
                </Badge>
              </TableCell>
              <TableCell className="text-right">
                <div className="flex justify-end gap-2">
                  <Button
                    variant="destructive"
                    size="sm"
                    disabled={isUpdating || approval === TimeTrackingApproval.Rejected}
                    onClick={() => onUpdateStatus(entry, TimeTrackingApproval.Rejected)}
                  >
                    {t('buttons.reject', { ns: 'common' })}
                  </Button>
                  <Button
                    size="sm"
                    disabled={isUpdating || approval === TimeTrackingApproval.Approved}
                    onClick={() => onUpdateStatus(entry, TimeTrackingApproval.Approved)}
                  >
                    {t('buttons.approve', { ns: 'common' })}
                  </Button>
                </div>
              </TableCell>
            </TableRow>
          )
          })}
        </LoaderTable>
      </TableBody>
    </Table>
  )
}
